import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser, NgClass } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../core/services/api.service';
import { API_ROUTES } from '../../../core/constants/api-routes';
import {
  SchedulePromptItem,
  SchedulePromptListingData,
  SchedulePromptListRequest,
  PromptSummaryStats,
  FamilyMember,
} from '../../../core/models/schedule-prompt.models';
import { MasterItem, StoryCategory } from '../../../core/models/story.models';

/** Number of prompts loaded per page. */
const PAGE_SIZE = 10;

/** schedulePromptStatusList ids (from GetAllMasterList typeId=8) relevant to this page's filter/KPI. */
const SCHEDULE_STATUS_ACTIVE = 1;
const SCHEDULE_STATUS_COMPLETED = 3;
const SCHEDULE_STATUS_STOPPED = 5;

@Component({
  selector: 'app-schedule-prompt',
  imports: [RouterLink, FormsModule, NgClass],
  templateUrl: './schedule-prompt.html',
  styleUrl: './schedule-prompt.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchedulePrompt implements OnInit, OnDestroy {
  // ── Dependency injection ─────────────────────────────────────────────────
  private readonly api = inject(ApiService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly zone = inject(NgZone);
  readonly allMembersAlreadyAdded = signal(false);

  // ── UI state ─────────────────────────────────────────────────────────────
  /** True until the very first load completes (suppresses premature empty states). */
  readonly isInitializing = signal(true);
  /** True when the API call returned a failure / network error. */
  readonly hasError = signal(false);
  /** True while an incremental (scroll-triggered) load is in progress. */
  readonly isLoadingMore = signal(false);

  // ── Server data ──────────────────────────────────────────────────────────
  /** Prompt cards returned by the current listing API call. */
  readonly prompts = signal<SchedulePromptItem[]>([]);
  /** Total matching records across all pages (from API). */
  readonly totalRecords = signal(0);
  /** True when more prompts remain beyond the currently loaded set. */
  readonly hasMorePrompts = signal(false);
  /** True once any API call returned totalRecords > 0 (for empty-state discrimination). */
  readonly hasPromptsInSystem = signal(false);
  /** Summary stats from the listing response. */
  readonly summaryStats = signal<PromptSummaryStats>({
    totalSchedules: 0,
    overallResponseRate: 0,
    totalStoriesCreated: 0,
    activeRecipients: 0,
  });
  /** Category list for the Category filter dropdown. */
  readonly categories = signal<StoryCategory[]>([]);

  // ── Active filter state ──────────────────────────────────────────────────
  /** Current value of the search input — two-way bound via [(ngModel)]. */
  customSearch = '';
  /** The search term actually submitted. Kept separate from customSearch. */
  readonly submittedSearch = signal('');
  /** Selected category ID. null = All. */
  readonly activeCategoryId = signal<number | null>(null);
  /** Selected schedule status. null = All. Defaults to Active on load. */
  readonly activeStatus = signal<number | null>(SCHEDULE_STATUS_ACTIVE);
  /** Selected frequency/type. null = All. */
  readonly activeType = signal<string | null>(null);
  /** Current page number (1-based). */
  readonly pageNumber = signal(1);

  /** Status filter options — loaded from GetAllMasterList typeId=8. */
  readonly statusOptions = signal<MasterItem[]>([]);
  /** Prompt ownership / type options — loaded from GetAllMasterList typeId=9. */
  readonly typeOptions = signal<MasterItem[]>([]);

  // ── Private ──────────────────────────────────────────────────────────────
  /** Guard that prevents a second concurrent request from firing. */
  private isRequestInFlight = false;
  /** IntersectionObserver that fires lazy loads when the sentinel scrolls into view. */
  private scrollObserver?: IntersectionObserver;
  readonly stoppingPromptId = signal<string | null>(null);
  readonly isStopping = signal(false);
  readonly stopError = signal<string | null>(null);

  // Remove member
  readonly removingMember = signal<{ promptId: string; memberId: string; name: string } | null>(
    null,
  );
  readonly isRemovingMember = signal(false);
  readonly removeMemberError = signal<string | null>(null);

  // Add member
  readonly addMemberPrompt = signal<{ promptId: string; existingMemberIds: string[] } | null>(null);
  readonly familyMembers = signal<FamilyMember[]>([]);
  readonly isLoadingFamilyMembers = signal(false);
  readonly selectedMemberIds = signal<string[]>([]);
  readonly isAddingMember = signal(false);
  readonly addMemberError = signal<string | null>(null);

  // ─────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ─────────────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.initPage();
  }

  ngOnDestroy(): void {
    this.scrollObserver?.disconnect();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Initialisation
  // ─────────────────────────────────────────────────────────────────────────

  private async initPage(): Promise<void> {
    await Promise.all([this.loadCategories(), this.loadMasterData()]);
    if (!this.activeType() && this.typeOptions().length) {
      this.activeType.set(this.typeOptions()[0].name);
    }
    await this.loadPrompts();
  }

  /**
   * Fetches schedule-prompt status (typeId=8) and ownership/type (typeId=9)
   * in one request and populates the two filter dropdown signals.
   */
  private async loadMasterData(): Promise<void> {
    const resp = await this.api.request<any>(
      'POST',
      API_ROUTES.COMMON_GET_ALL_MASTER_LIST,
      { typeIds: [8, 9] },
      { showToaster: false, showLoader: true },
    );
    if (resp.status && resp.data) {
      this.statusOptions.set(resp.data.schedulePromptStatusList ?? []);
      this.typeOptions.set(resp.data.promptOwnershipTypeList ?? []);
    }
  }

  /** Fetches category list for the Category dropdown filter. */
  private async loadCategories(): Promise<void> {
    const resp = await this.api.request<StoryCategory[]>(
      'GET',
      API_ROUTES.SP_GET_CATEGORIES,
      undefined,
      { showToaster: false, showLoader: true },
    );
    if (resp.status && resp.data) {
      const list: StoryCategory[] = Array.isArray(resp.data)
        ? resp.data
        : ((resp.data as any).categories ?? []);
      this.categories.set(list);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Prompts API call
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Fetches prompts. When isLoadMore=false (default) it replaces the list
   * and re-attaches the scroll observer. When isLoadMore=true it appends.
   */
  async loadPrompts(isLoadMore = false): Promise<void> {
    if (this.isRequestInFlight) return;
    if (isLoadMore && !this.hasMorePrompts()) return;

    this.isRequestInFlight = true;

    if (isLoadMore) {
      this.isLoadingMore.set(true);
    } else {
      this.hasError.set(false);
    }

    const request: SchedulePromptListRequest = {
      page: this.pageNumber(),
      pageSize: PAGE_SIZE,
      search: this.submittedSearch(),
      type: this.activeType(),
      status: this.activeStatus(),
      categoryId: this.activeCategoryId(),
    };

    try {
      const resp = await this.api.request<SchedulePromptListingData>(
        'POST',
        API_ROUTES.SP_GET_PROMPT_LIST,
        request,
        { showToaster: false, showLoader: !isLoadMore, useToken: true },
      );

      if (resp.status && resp.data) {
        const incoming = Array.isArray(resp.data.prompts) ? resp.data.prompts : [];
        const total = resp.data.totalRecords ?? 0;

        if (isLoadMore) {
          this.prompts.update((existing) => [...existing, ...incoming]);
        } else {
          this.prompts.set(incoming);
        }

        this.totalRecords.set(total);
        if (total > 0) this.hasPromptsInSystem.set(true);
        this.hasMorePrompts.set(this.prompts().length < total);

        if (resp.data.summaryStats && !isLoadMore) {
          this.summaryStats.set(resp.data.summaryStats);
        }
      } else {
        if (!isLoadMore) {
          this.prompts.set([]);
          this.totalRecords.set(0);
          this.hasError.set(true);
        }
        this.hasMorePrompts.set(false);
      }
    } catch {
      if (!isLoadMore) {
        this.hasError.set(true);
        this.prompts.set([]);
        this.totalRecords.set(0);
      }
      this.hasMorePrompts.set(false);
    } finally {
      this.isLoadingMore.set(false);
      this.isRequestInFlight = false;
      this.isInitializing.set(false);

      if (!isLoadMore && isPlatformBrowser(this.platformId)) {
        setTimeout(() => this.setupScrollObserver(), 100);
      }
    }
  }

  /** Attaches an IntersectionObserver to the bottom sentinel element. */
  private setupScrollObserver(): void {
    this.scrollObserver?.disconnect();

    const sentinel = document.querySelector('#promptsScrollSentinel');
    if (!sentinel) return;

    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.isRequestInFlight && this.hasMorePrompts()) {
          this.zone.run(() => {
            this.pageNumber.update((n) => n + 1);
            this.loadPrompts(true);
          });
        }
      },
      { threshold: 0.1 },
    );

    this.scrollObserver.observe(sentinel);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // User interaction handlers
  // ─────────────────────────────────────────────────────────────────────────

  async onSearch(): Promise<void> {
    const term = this.customSearch?.trim() ?? '';
    this.customSearch = term;
    this.submittedSearch.set(term);
    this.pageNumber.set(1);
    await this.loadPrompts();
  }

  async clearSearch(): Promise<void> {
    this.customSearch = '';
    this.submittedSearch.set('');
    this.pageNumber.set(1);
    await this.loadPrompts();
  }

  onCategoryChange(value: string): void {
    this.activeCategoryId.set(value === '' ? null : Number(value));
    this.pageNumber.set(1);
    this.loadPrompts();
  }

  /** Full name of the currently selected category, for the select's title tooltip. */
  getSelectedCategoryName(): string {
    const id = this.activeCategoryId();
    if (id === null) return 'All Category';
    return this.categories().find((c) => c.categoryId === id)?.categoryName ?? 'All Category';
  }

  /** Label for the first KPI card — tracks whichever status is currently filtered. */
  getFirstKpiLabel(): string {
    switch (this.activeStatus()) {
      case SCHEDULE_STATUS_ACTIVE:
        return 'Active Schedules';
      case SCHEDULE_STATUS_COMPLETED:
        return 'Completed Schedules';
      case SCHEDULE_STATUS_STOPPED:
        return 'Stopped Schedules';
      default:
        return 'Total Scheduled';
    }
  }

  /** Stopped prompts have no active recipients to speak of — hide that KPI card for that filter. */
  readonly showActiveRecipientsKpi = computed(
    () => this.activeType() !== 'Invited' && this.activeStatus() !== SCHEDULE_STATUS_STOPPED,
  );

  onStatusChange(value: string): void {
    this.activeStatus.set(value === '' ? null : Number(value));
    this.pageNumber.set(1);
    this.loadPrompts();
  }

  onTypeChange(value: string): void {
    this.activeType.set(value === '' ? null : value);

    // Switching ownership tabs (Created/Invited) starts a fresh view —
    // reset the other filters back to "All" instead of carrying them over.
    this.customSearch = '';
    this.submittedSearch.set('');
    this.activeCategoryId.set(null);
    this.activeStatus.set(SCHEDULE_STATUS_ACTIVE);

    this.pageNumber.set(1);
    this.loadPrompts();
  }

  /** Short display label for a promptOwnershipType tab (e.g. "CreatedByMe" -> "Created"). */
  getTabLabel(name: string): string {
    return name === 'CreatedByMe' ? 'Created' : name;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Template utility helpers
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Formats an ISO date string to a short readable date (no time).
   * e.g. "Jun 10, 2026"
   */
  formatNextDelivery(dateString: string | null): string {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  }

  /**
   * Returns a CSS badge class based on the scheduleStatus value.
   */
  getStatusBadgeClass(status: number): string {
    switch (status) {
      case 1:
        return 'fm-badge-active'; // Active    → Green
      case 2:
        return 'fm-badge-paused'; // Paused    → Yellow/Amber
      case 3:
        return 'fm-badge-completed'; // Completed → Blue
      case 4:
        return 'fm-badge-delivered'; // Delivered → Teal/Cyan
      case 5:
        return 'fm-badge-stopped'; // Stopped   → Orange
      case 6:
        return 'fm-badge-deleted'; // Delete    → Red
      case 7:
        return 'fm-badge-scheduled'; // Scheduled → Purple
      default:
        return 'fm-badge-secondary';
    }
  }

  /**
   * Returns initials from a display name (up to 2 letters).
   */
  getMemberInitials(name: string | undefined): string {
    if (!name?.trim()) return '?';
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('');
  }

  /**
   * Returns a 1-based sequential index for a prompt in the cumulative list.
   */
  getPromptIndex(index: number): string {
    return String(index + 1).padStart(2, '0');
  }

  openStopConfirm(promptId: string): void {
    this.stoppingPromptId.set(promptId);
    this.stopError.set(null);
  }

  cancelStop(): void {
    this.stoppingPromptId.set(null);
    this.stopError.set(null);
  }

  async confirmStop(): Promise<void> {
    const id = this.stoppingPromptId();
    if (!id) return;

    this.isStopping.set(true);
    this.stopError.set(null);

    try {
      const resp = await this.api.request<boolean>(
        'POST',
        API_ROUTES.SP_STOP_PROMPT,
        { schedulePromptId: id },
        { showToaster: true, showLoader: false, useToken: true },
      );

      if (resp.status) {
        this.stoppingPromptId.set(null);
        this.pageNumber.set(1);
        await this.loadPrompts();
      } else {
        this.stopError.set(resp.message ?? 'Failed to stop. Please try again.');
      }
    } catch {
      this.stopError.set('An unexpected error occurred. Please try again.');
    } finally {
      this.isStopping.set(false);
    }
  }

  openRemoveMemberConfirm(promptId: string, memberId: string, name: string): void {
    this.removingMember.set({ promptId, memberId, name });
    this.removeMemberError.set(null);
  }

  cancelRemoveMember(): void {
    this.removingMember.set(null);
    this.removeMemberError.set(null);
  }

  async confirmRemoveMember(): Promise<void> {
    const data = this.removingMember();
    if (!data) return;

    this.isRemovingMember.set(true);
    this.removeMemberError.set(null);

    try {
      const resp = await this.api.request<boolean>(
        'POST',
        API_ROUTES.SP_UPDATE_PROMPT,
        { schedulePromptId: data.promptId, addMemberIds: null, removeMemberIds: [data.memberId] },
        { showToaster: true, showLoader: false, useToken: true },
      );

      if (resp.status) {
        this.removingMember.set(null);
        this.pageNumber.set(1);
        await this.loadPrompts();
      } else {
        this.removeMemberError.set(resp.message ?? 'Failed to remove member. Please try again.');
      }
    } catch {
      this.removeMemberError.set('An unexpected error occurred. Please try again.');
    } finally {
      this.isRemovingMember.set(false);
    }
  }

  // ── Add Member ────────────────────────────────────────────────────────────

  async openAddMember(promptId: string, existingMemberIds: string[] = []): Promise<void> {
    this.addMemberPrompt.set({ promptId, existingMemberIds });
    this.selectedMemberIds.set([]);
    this.addMemberError.set(null);
    this.allMembersAlreadyAdded.set(false); // reset
    this.isLoadingFamilyMembers.set(true);

    try {
      const resp = await this.api.request<FamilyMember[]>(
        'GET',
        API_ROUTES.SP_GET_FAMILY_MEMBERS,
        undefined,
        { showToaster: false, showLoader: false, useToken: true },
      );
      if (resp.status && resp.data) {
        const members = Array.isArray(resp.data) ? resp.data : [];
        this.familyMembers.set(members);

        // Check if every member is already added
        const availableCount = members.filter(
          (m) => !existingMemberIds.includes(m.familyMemberId),
        ).length;
        this.allMembersAlreadyAdded.set(availableCount === 0);
      }
    } catch {
      this.familyMembers.set([]);
    } finally {
      this.isLoadingFamilyMembers.set(false);
    }
  }

  cancelAddMember(): void {
    this.addMemberPrompt.set(null);
    this.selectedMemberIds.set([]);
    this.addMemberError.set(null);
    this.allMembersAlreadyAdded.set(false); // add this
  }

  toggleMemberSelection(memberId: string): void {
    const current = this.selectedMemberIds();
    if (current.includes(memberId)) {
      this.selectedMemberIds.set(current.filter((id) => id !== memberId));
    } else {
      this.selectedMemberIds.set([...current, memberId]);
    }
  }

  async confirmAddMember(): Promise<void> {
    const data = this.addMemberPrompt();
    const ids = this.selectedMemberIds();
    if (!data || ids.length === 0) return;

    this.isAddingMember.set(true);
    this.addMemberError.set(null);

    try {
      const resp = await this.api.request<boolean>(
        'POST',
        API_ROUTES.SP_UPDATE_PROMPT,
        { schedulePromptId: data.promptId, addMemberIds: ids, removeMemberIds: [] },
        { showToaster: true, showLoader: false, useToken: true },
      );

      if (resp.status) {
        this.addMemberPrompt.set(null);
        this.selectedMemberIds.set([]);
        this.pageNumber.set(1);
        await this.loadPrompts();
      } else {
        this.addMemberError.set(resp.message ?? 'Failed to add members. Please try again.');
      }
    } catch {
      this.addMemberError.set('An unexpected error occurred. Please try again.');
    } finally {
      this.isAddingMember.set(false);
    }
  }

  isMemberAlreadyAdded(memberId: string, prompt: SchedulePromptItem): boolean {
    return prompt.sentToMembers?.some((m) => m.memberId === memberId) ?? false;
  }
  getMemberIds(prompt: SchedulePromptItem): string[] {
    return prompt.sentToMembers?.map((m) => m.memberId) ?? [];
  }
}
